import { useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  buyerSequenceApi,
  type BuyerSequenceBlockKey,
  type BuyerSequenceSelection,
  type BuyerSequenceWordingDto,
} from "@/api/buyerSequence";
import { buyerSequenceKeys } from "@/hooks/buyers/buyerSequenceKeys";
import { useDebounced } from "@/hooks/buyers/useSmartImport";

/**
 * Reads and writes for the Outreach tab's Edit wording mode.
 *
 * 🔴 EVERY WRITE HERE INVALIDATES `buyerSequenceKeys.all`, NOT ONLY THE KEY IT
 * CHANGED. Wording and call hours belong to the CURRENT USER, not to a buyer, so
 * one save re-renders every buyer's messages (and call hours move the
 * availability line in all of them). A narrower invalidation would leave another
 * buyer's send view showing the old wording for minutes.
 *
 * The write also seeds the wording cache with the server's answer, so the editor
 * shows what was stored before the refetch lands. The invalidation is not
 * awaited: the button stops spinning when the save is stored, not when every
 * open buyer view has been refetched.
 */

export function useWording(templateKey: string, enabled = true) {
  return useQuery({
    queryKey: buyerSequenceKeys.wording(templateKey),
    queryFn: () => buyerSequenceApi.getWording(templateKey),
    enabled,
  });
}

export function useWordingFields() {
  return useQuery({
    queryKey: buyerSequenceKeys.fields(),
    queryFn: () => buyerSequenceApi.listFields(),
    // The field list is a code constant on the server.
    staleTime: Infinity,
  });
}

export function useCallHours() {
  return useQuery({
    queryKey: buyerSequenceKeys.callHours(),
    queryFn: () => buyerSequenceApi.getCallHours(),
  });
}

export const PREVIEW_DEBOUNCE_MS = 400;

/**
 * The live preview: the WORKING blocks rendered on this buyer.
 *
 * Debounced on the serialised blocks rather than on the object, so a parent
 * re-render that rebuilds an identical object does not restart the timer. The
 * first render is not delayed — `useDebounced` starts from the value it is given.
 */
export function useWordingPreview(args: {
  buyerId: string;
  templateKey: string;
  blocks: Record<string, string> | null;
  selection: BuyerSequenceSelection;
  enabled: boolean;
}) {
  const { buyerId, templateKey, blocks, selection, enabled } = args;
  const serialised = useDebounced(blocks ? JSON.stringify(blocks) : "", PREVIEW_DEBOUNCE_MS);
  const settled = useMemo<Record<string, string> | null>(
    () => (serialised ? (JSON.parse(serialised) as Record<string, string>) : null),
    [serialised],
  );
  return useQuery({
    queryKey: buyerSequenceKeys.preview(buyerId, templateKey, settled ?? {}, selection),
    queryFn: () =>
      buyerSequenceApi.preview(buyerId, {
        templateKey,
        blocks: settled ?? {},
        listingId: selection.listingId ?? null,
        noListing: !!selection.noListing,
      }),
    enabled: enabled && settled !== null,
    placeholderData: keepPreviousData,
  });
}

// ── Writes ───────────────────────────────────────────────────────────────────

export function useSaveTeamDraft(templateKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { blockKey: BuyerSequenceBlockKey; text: string | null }) =>
      buyerSequenceApi.saveTeamDraft(templateKey, v.blockKey, v.text),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.wording(templateKey), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

export function useDiscardTeamDraft(templateKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => buyerSequenceApi.discardTeamDraft(templateKey),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.wording(templateKey), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

export function usePublishTeam(templateKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string | null) => buyerSequenceApi.publishTeam(templateKey, note),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.wording(templateKey), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

export function useRollbackTeam(templateKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => buyerSequenceApi.rollbackTeam(templateKey, version),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.wording(templateKey), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

export function useSavePersonal(templateKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blocks: Record<string, string>) =>
      buyerSequenceApi.savePersonal(templateKey, blocks),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.wording(templateKey), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

export function useDeletePersonal(templateKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => buyerSequenceApi.deletePersonal(templateKey),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.wording(templateKey), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

export function useSaveCallHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { startMinutes: number; endMinutes: number }) =>
      buyerSequenceApi.saveCallHours(v.startMinutes, v.endMinutes),
    onSuccess: (data) => {
      qc.setQueryData(buyerSequenceKeys.callHours(), data);
      void qc.invalidateQueries({ queryKey: buyerSequenceKeys.all });
    },
  });
}

// ── Working copy ─────────────────────────────────────────────────────────────

export type BlockMap = Record<string, string>;

/** What the team editor starts from: the unpublished draft where one exists,
 *  else the wording in force. */
export function teamBaseline(w: BuyerSequenceWordingDto): BlockMap {
  return Object.fromEntries(w.blocks.map((b) => [b.blockKey, b.teamDraftText ?? b.teamText]));
}

/** What "My version" starts from: the user's own text, else the team wording
 *  they would otherwise be sending. */
export function personalBaseline(w: BuyerSequenceWordingDto): BlockMap {
  return Object.fromEntries(w.blocks.map((b) => [b.blockKey, b.personalText ?? b.teamText]));
}

interface WorkingState {
  key: string | null;
  base: BlockMap | null;
  working: BlockMap | null;
}

/**
 * The textareas' contents, following the server where the person has not typed.
 *
 * 🔴 A BLOCK THE PERSON HAS EDITED IS NEVER OVERWRITTEN BY A REFETCH. Every save
 * invalidates `buyerSequenceKeys.all`, which refetches the wording this editor is
 * reading; re-seeding from it would wipe the half-typed text in every OTHER
 * block. So when the baseline moves, a block whose working text still equals the
 * OLD baseline follows the new one, and an edited block keeps its text. After a
 * successful save the edited block equals the new baseline, which is exactly
 * "saved" — no per-mutation bookkeeping needed.
 *
 * The follow happens during render (React's "previous value" pattern), not in an
 * effect, so no frame paints the stale text.
 */
export function useWorkingBlocks(baseline: BlockMap | null) {
  const key = baseline ? JSON.stringify(baseline) : null;
  const [state, setState] = useState<WorkingState>({ key, base: baseline, working: baseline });

  let current = state;
  if (state.key !== key) {
    const followed =
      baseline && state.working && state.base
        ? Object.fromEntries(
            Object.entries(baseline).map(([k, v]) => {
              const typed = state.working?.[k];
              const untouched = typed === undefined || typed === state.base?.[k];
              return [k, untouched ? v : (typed as string)];
            }),
          )
        : baseline;
    current = { key, base: baseline, working: followed };
    setState(current);
  }

  const working = current.working;
  const base = current.base;

  const dirtyKeys = useMemo(
    () =>
      working && base ? Object.keys(working).filter((k) => working[k] !== base[k]) : [],
    [working, base],
  );

  return {
    working,
    dirtyKeys,
    setBlock: (blockKey: string, text: string) =>
      setState((s) => (s.working ? { ...s, working: { ...s.working, [blockKey]: text } } : s)),
    /** Throw away every unsaved edit. */
    reset: () => setState((s) => ({ ...s, working: s.base })),
  };
}

// ── Call hours ───────────────────────────────────────────────────────────────

/** `780` → `"13:00"`, the value an `<input type="time">` takes. */
export function minutesToTimeInput(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** `"13:00"` → `780`; null for anything that is not a 24-hour `HH:MM`. */
export function timeInputToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** `780` → `"1:00 PM"` for prose. */
export function minutesToClock(minutes: number): string {
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}
